import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Popover, PopoverBody } from 'reactstrap';
import vlog from '../javascript/vlog';

function ReporterDetailsPopover({
  reporter,
  targetId,
  placement,
  children,
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [v2MediaProfile, setV2MediaProfile] = useState(null);
  const [loadingV2Profile, setLoadingV2Profile] = useState(false);

  const togglePopover = () => {
    setPopoverOpen(!popoverOpen);
  };

  const loadV2MediaProfile = useCallback(async () => {
    if (!reporter || !reporter.id) return;

    setLoadingV2Profile(true);

    try {
      const { supabaseClient } = window;
      const { data, error } = await supabaseClient
        .from('media_profiles')
        .select('*')
        .eq('migrate_id', reporter.id)
        .single();

      if (error) {
        vlog.error('Error loading V2 media profile:', error);
        setLoadingV2Profile(false);
        return;
      }

      setV2MediaProfile(data);
      setLoadingV2Profile(false);
    } catch (error) {
      vlog.error('Error loading V2 media profile:', error);
      setLoadingV2Profile(false);
    }
  }, [reporter]);

  useEffect(() => {
    loadV2MediaProfile();
  }, [reporter?.id, loadV2MediaProfile]);

  const renderPopoverContent = () => {
    if (loadingV2Profile) {
      return null;
    }

    if (!v2MediaProfile) {
      return <div className="text-muted">No additional details available</div>;
    }

    const getContactIcon = (type) => {
      const iconMap = {
        linkedin: 'fa-brands fa-linkedin',
        twitter: 'fa-brands fa-x-twitter',
        facebook: 'fa-brands fa-facebook',
        instagram: 'fa-brands fa-instagram',
        bluesky: 'fa-brands fa-bluesky',
        url: 'fa-solid fa-globe',
      };
      return iconMap[type] || 'fa-solid fa-link';
    };

    const getContactLabel = (type) => {
      const labelMap = {
        linkedin: 'LinkedIn',
        twitter: 'Twitter',
        facebook: 'Facebook',
        instagram: 'Instagram',
        bluesky: 'Bluesky',
        url: 'Website',
      };
      return labelMap[type] || 'Contact';
    };

    return (
      <div>
        {/* Name - largest */}
        {v2MediaProfile.name && (
          <div className="fs-4 fw-bold text-dark mb-2">
            {v2MediaProfile.name}
          </div>
        )}

        {/* Headline - second largest */}
        {v2MediaProfile.headline && (
          <div className="fs-6 fw-semibold text-dark mb-3">
            {v2MediaProfile.headline}
          </div>
        )}

        {/* Bio - paragraph */}
        {v2MediaProfile.bio && (
          <div className="text-dark mb-3" style={{ lineHeight: '1.4' }}>
            {v2MediaProfile.bio.length > 150
              ? `${v2MediaProfile.bio.substring(0, 150)}...`
              : v2MediaProfile.bio}
          </div>
        )}

        {/* Contact details */}
        {v2MediaProfile.contact_details && v2MediaProfile.contact_details.length > 0 && (
          <div className="mt-3 pt-2 border-top">
            {v2MediaProfile.contact_details.map((contact, index) => {
              const supportedTypes = ['linkedin', 'twitter', 'facebook', 'instagram', 'url', 'bluesky'];
              if (supportedTypes.includes(contact.type) && contact.value) {
                return (
                  <div key={index} className="mb-1">
                    <a
                      href={contact.value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="small text-muted text-decoration-underline"
                      style={{
                        display: 'block',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.cursor = 'pointer';
                      }}
                    >
                      <i className={`${getContactIcon(contact.type)} me-2`} />
                      {getContactLabel(contact.type)}
                    </a>
                  </div>
                );
              }
              return null;
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {children}
      {(!loadingV2Profile && v2MediaProfile) && (
        <Popover
          placement={placement}
          isOpen={popoverOpen}
          target={targetId}
          trigger="hover"
          toggle={togglePopover}
        >
          <PopoverBody>
            {renderPopoverContent()}
          </PopoverBody>
        </Popover>
      )}
    </>
  );
}

ReporterDetailsPopover.propTypes = {
  reporter: PropTypes.object.isRequired,
  targetId: PropTypes.string.isRequired,
  placement: PropTypes.string,
  children: PropTypes.node,
};

ReporterDetailsPopover.defaultProps = {
  placement: 'right',
  children: null,
};

export default ReporterDetailsPopover;
